import { FieldTextCompact, InfoTooltip } from '@stratumn/atomic';
import { Add, Pen, Save, TimesCircle, Trash } from '@stratumn/icons';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import injectSheet from 'react-jss';
import validator from 'validator';

import styles from './mailingList.style';

const { isEmail } = validator;

export const MailingList = ({
  canUpdateOrganization,
  email,
  onUpdate,
  classes
}) => {
  const [mailingListEmail, setMailingListEmail] = useState(email || '');
  const [editEmail, setEditEmail] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(isEmail(email));

  useEffect(() => {
    const emailIsValid = isEmail(mailingListEmail);
    setIsDirty(email !== mailingListEmail);
    setIsValidEmail(emailIsValid);
  }, [mailingListEmail, email]);

  const handleAddClick = () => {
    onUpdate(mailingListEmail).catch(() => {
      setMailingListEmail(email);
    });
    setEditEmail(!editEmail);
  };

  const handleChange = useCallback(e => {
    const newValue = e.target.value;
    setMailingListEmail(newValue);
  }, []);

  const handleDelete = () => {
    setMailingListEmail('');
    onUpdate('').catch(() => {});
    setEditEmail(!editEmail);
  };

  const toggleEditButton = () => {
    setEditEmail(!editEmail);
    if (isDirty) {
      setMailingListEmail(email);
    }
  };

  if (!(mailingListEmail || canUpdateOrganization)) return null;

  return (
    <div className={classes.container}>
      {!editEmail &&
        (mailingListEmail ? (
          <p>
            Notification mailing list:
            {canUpdateOrganization ? (
              <button
                className={classes.button}
                data-testid="edit-button"
                name="Edit email"
                onClick={toggleEditButton}
                type="button"
              >
                <span data-testid="email-info">
                  {mailingListEmail}
                  <Pen className={classes.editIcon} />
                </span>
              </button>
            ) : (
              <span data-testid="email-info">{mailingListEmail}</span>
            )}
          </p>
        ) : (
          <button
            className={classes.addEmail}
            data-testid="add-email-button"
            name="Add email"
            onClick={toggleEditButton}
            type="button"
          >
            Set mailing list for notifications
            <Add />
          </button>
        ))}

      {canUpdateOrganization && editEmail && (
        <div className={classes.edit}>
          <FieldTextCompact
            data-testid="email-input"
            invalid={isDirty && !isValidEmail}
            label=""
            onValueChange={handleChange}
            value={mailingListEmail}
          />

          <button
            className={classes.button}
            data-testid="save-button"
            disabled={!(isDirty && isValidEmail)}
            name="Save email"
            onClick={handleAddClick}
            type="submit"
          >
            <InfoTooltip
              backgroundColor="grey"
              delay={200}
              position={{
                place: 'above',
                placeShift: 3,
                adjustPlace: true,
                anchor: 'center'
              }}
              text="Save the email"
              textColor="#fff"
            >
              <Save />
            </InfoTooltip>
          </button>

          <button
            className={classes.button}
            data-testid="cancel-button"
            name="Cancel"
            onClick={toggleEditButton}
            type="reset"
          >
            <InfoTooltip
              backgroundColor="grey"
              delay={200}
              position={{
                place: 'above',
                placeShift: 3,
                adjustPlace: true,
                anchor: 'center'
              }}
              text="Cancel and close"
              textAlign="center"
              textColor="#fff"
            >
              <TimesCircle />
            </InfoTooltip>
          </button>
          <button
            className={classes.button}
            data-testid="delete-button"
            name="Delete"
            onClick={handleDelete}
            type="submit"
          >
            <InfoTooltip
              backgroundColor="grey"
              delay={200}
              position={{
                place: 'above',
                placeShift: 3,
                adjustPlace: true,
                anchor: 'center'
              }}
              text="Remove the email"
              textColor="#fff"
            >
              <Trash />
            </InfoTooltip>
          </button>
        </div>
      )}
    </div>
  );
};

MailingList.propTypes = {
  classes: PropTypes.object.isRequired,
  canUpdateOrganization: PropTypes.bool.isRequired,
  email: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default injectSheet(styles)(MailingList);
